import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Bug, ChevronDown, Loader2, MessageSquare, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuthedServerFn } from "@/lib/server-fn-auth";
import { Button } from "@/components/ui/button";
import {
  listMyBugReports,
  getMyBugReportThread,
  type MyBugReportRow,
  type MyBugReportThread,
} from "@/lib/bug-reports-reporter.functions";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/my-reports")({
  component: MyReportsPage,
  head: () => ({ meta: [{ title: "My bug reports" }] }),
});

const STATUS_LABELS: Record<string, string> = {
  open: "Received",
  investigating: "Looking into it",
  fixed: "Fixed",
  wont_fix: "Won't fix",
  duplicate: "Duplicate",
};

function statusColor(s: string) {
  if (s === "open") return "bg-amber-500/15 text-amber-700 border-amber-500/30 dark:text-amber-300";
  if (s === "investigating") return "bg-blue-500/15 text-blue-700 border-blue-500/30 dark:text-blue-300";
  if (s === "fixed") return "bg-emerald-500/15 text-emerald-700 border-emerald-500/30 dark:text-emerald-300";
  return "bg-muted text-muted-foreground border-border";
}

function formatRelative(ts: string) {
  const d = new Date(ts);
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

function MyReportsPage() {
  const listFn = useAuthedServerFn(listMyBugReports);
  const threadFn = useAuthedServerFn(getMyBugReportThread);

  const [entries, setEntries] = useState<MyBugReportRow[] | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [threads, setThreads] = useState<Record<string, MyBugReportThread | "loading">>({});

  const load = useCallback(async () => {
    try {
      const r = await listFn();
      setEntries(r.entries);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load");
    }
  }, [listFn]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = useCallback(
    async (id: string) => {
      if (expanded === id) {
        setExpanded(null);
        return;
      }
      setExpanded(id);
      if (!threads[id] || threads[id] === "loading") {
        setThreads((m) => ({ ...m, [id]: "loading" }));
        try {
          const t = await threadFn({ data: { id } });
          setThreads((m) => ({ ...m, [id]: t }));
          // Optimistically clear unread flag on this row
          setEntries((rows) =>
            rows
              ? rows.map((r) =>
                  r.id === id
                    ? { ...r, is_unread: false, last_seen_status: r.status }
                    : r,
                )
              : rows,
          );
        } catch (e) {
          toast.error(e instanceof Error ? e.message : "Failed to load thread");
          setThreads((m) => {
            const next = { ...m };
            delete next[id];
            return next;
          });
        }
      }
    },
    [expanded, threadFn, threads],
  );

  return (
    <main className="max-w-3xl mx-auto px-4 py-8">
      <header className="mb-6">
        <Link
          to="/home"
          className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2"
        >
          <ArrowLeft className="w-3 h-3" /> Home
        </Link>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2">
          <Bug className="w-6 h-6 text-primary" /> My bug reports
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Bugs you've reported, and any replies from the team.
        </p>
      </header>

      {entries === null ? (
        <div className="p-12 text-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : entries.length === 0 ? (
        <div className="rounded-xl border border-border bg-card p-10 text-center text-sm text-muted-foreground">
          You haven't reported any bugs yet.
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((e) => {
            const isOpen = expanded === e.id;
            const thread = threads[e.id];
            return (
              <div
                key={e.id}
                className={cn(
                  "rounded-xl border bg-card transition-colors",
                  e.is_unread
                    ? "border-primary/50 ring-1 ring-primary/20"
                    : "border-border",
                )}
              >
                <button
                  onClick={() => void toggle(e.id)}
                  className="w-full text-left p-4 flex items-start gap-3"
                >
                  {e.is_unread && (
                    <span
                      className="mt-1.5 inline-block h-2 w-2 rounded-full bg-primary shrink-0"
                      aria-label="Unread update"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <span className={cn("px-2 py-0.5 rounded border text-[10px]", statusColor(e.status))}>
                        {STATUS_LABELS[e.status] ?? e.status}
                      </span>
                      {e.message_count > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
                          <MessageSquare className="w-3 h-3" /> {e.message_count}
                        </span>
                      )}
                      {e.has_screenshot && <ImageIcon className="w-3 h-3 text-muted-foreground" />}
                      {e.source === "error_boundary" && (
                        <span className="px-1.5 py-0 rounded text-[10px] bg-destructive/10 text-destructive border border-destructive/30">
                          crash
                        </span>
                      )}
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        {formatRelative(e.latest_activity_at)}
                      </span>
                    </div>
                    <div className="text-sm font-medium line-clamp-2">
                      {e.description || "(no description)"}
                    </div>
                  </div>
                  <ChevronDown
                    className={cn(
                      "w-4 h-4 text-muted-foreground transition-transform mt-1 shrink-0",
                      isOpen && "rotate-180",
                    )}
                  />
                </button>

                {isOpen && (
                  <div className="border-t border-border p-4 space-y-4 text-sm">
                    {thread === "loading" || !thread ? (
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                      </div>
                    ) : (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                            Your report
                          </div>
                          <div className="rounded-md border border-border bg-muted/30 p-3 whitespace-pre-wrap">
                            {thread.description || "(no description)"}
                          </div>
                          <div className="mt-1 text-[10px] text-muted-foreground">
                            Submitted {new Date(thread.created_at).toLocaleString()}
                            {thread.url ? ` · on ${thread.url}` : ""}
                          </div>
                        </div>

                        {thread.screenshot_signed_url && (
                          <div>
                            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">
                              Screenshot
                            </div>
                            <a href={thread.screenshot_signed_url} target="_blank" rel="noreferrer">
                              <img
                                src={thread.screenshot_signed_url}
                                alt="Screenshot"
                                className="max-w-full rounded-md border border-border"
                              />
                            </a>
                          </div>
                        )}

                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                            Replies from the team
                          </div>
                          {thread.messages.length === 0 ? (
                            <div className="text-xs text-muted-foreground italic">
                              No replies yet. We'll post updates here as we look into it.
                            </div>
                          ) : (
                            <div className="space-y-2">
                              {thread.messages.map((m: MyBugReportThread["messages"][number]) => (
                                <div
                                  key={m.id}
                                  className="rounded-md border border-primary/20 bg-primary/5 p-3"
                                >
                                  <div className="whitespace-pre-wrap text-sm">{m.body}</div>
                                  <div className="mt-1 text-[10px] text-muted-foreground">
                                    {new Date(m.created_at).toLocaleString()}
                                    {m.updated_at !== m.created_at ? " · edited" : ""}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 text-center">
        <Button variant="outline" asChild>
          <Link to="/home">
            <ArrowLeft className="w-4 h-4" /> Back home
          </Link>
        </Button>
      </div>
    </main>
  );
}
